import * as React from 'react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import {
  SeriesClickEvent,
  LegendItemClickEvent,
} from '@progress/kendo-react-charts';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import {
  fetchActiveNodeTypeCounts,
  fetchStoreCount,
  fetchSystemTypes,
} from '../../store/Assets/AssetsActions';
import { CategoryCountResponseEntry } from '../../Interfaces/Assets/CategoryCountResponseEntry';
import 'hammerjs';
// Components
import { GetString } from '../StringResourceManager';
import WidgetTotalCountTile from '../WidgetTotalCountTile';
import {
  saveNodesFilters,
  saveNodesGridState,
} from '../../store/UserSettings/UserSettingsActions';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { AssetInfo } from '../../Interfaces/Assets/AssetInfo';
import { NodeTypeCounts } from '../../Interfaces/Assets/NodeTypeCounts';
import OverviewWidgetPieChart from '../OverviewWidgetPieChart';
import StringResourceManager from '../StringResourceManager';
import { ASSET } from '../../utils/constants';

// Styles
const useStylesAssetsOverview = makeStyles((theme: Theme) =>
  createStyles({
    widgetBox: {
      margin: theme.spacing(2),
      overflow: 'hidden',
      width: '460px',
    },
    flexBox: {
      flex: 1,
      display: 'flex',
    },
    widgetItem: {
      flex: 1,
    },
    chart: {
      border: '1px solid #ccc',
    },
    totalCountContainer: {
      display: 'flex',
      flexDirection: 'row',
      height: 140,
    },
    overViewTitle: {
      display: 'flex',
      justifyContent: 'center',
    },
  }),
);

export default function AssetsOverview() {
  const classes = useStylesAssetsOverview();
  const [refresh, setRefresh] = useState<boolean>(true);

  const storeRecordsCount: number = useSelector(
    (state: ApplicationState) => state.assetsReducer.storeCount,
  );

  const categoryRecordCounts: CategoryCountResponseEntry[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.categoryCountsOverview,
  );

  const isFetchingCategoryCountsOverview: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.isFetchingCategoryCountsOverview,
  );

  const nodeTypeCounts: NodeTypeCounts = useSelector(
    (state: ApplicationState) => state.assetsReducer.nodeTypeCounts,
  );

  const dispatch: Dispatch<any> = useDispatch();

  useEffect((): ReturnType<React.EffectCallback> => {
    if (refresh) {
      fetchStoreCount(dispatch);
      fetchSystemTypes(dispatch);
      fetchActiveNodeTypeCounts(dispatch);
      setRefresh(false);
    }
    return (): void => {};
  }, [refresh]);

  const labelContent = (props: any) => props.dataItem.Total;

  const onChartSeriesItemClick = (event: SeriesClickEvent) => {
    const dataItem: AssetInfo = event.dataItem;
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.FIELD_ASSETINFO_NAME,
          operator: 'eq',
          value: dataItem,
        },
      ],
    };
    saveNodesFilters(dispatch, filter);
  };

  const onLegendItemClick = (event: LegendItemClickEvent) => {
    let series: any = event.series;
    const dataItem: AssetInfo = series.data[event.pointIndex];
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.FIELD_ASSETINFO_NAME,
          operator: 'eq',
          value: dataItem,
        },
      ],
    };
    saveNodesFilters(dispatch, filter);
  };

  const resetNodesFilter = () => {
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [],
    };
    saveNodesFilters(dispatch, filter);
  };

  const resetGridState = () => {
    let gridState: State = {
      skip: 0,
      take: 100,
      filter: {
        logic: 'and',
        filters: [],
      },
    };
    saveNodesGridState(dispatch, gridState);
  };

  const widgetStoreCountTileClick = () => {
    resetNodesFilter();

    let gridState: State = {
      skip: 0,
      take: 100,
      filter: {
        logic: 'and',
        filters: [],
      },
      group: [{ field: 'SiteNumber' }],
    };
    saveNodesGridState(dispatch, gridState);
  };

  const widgetLaneCountTileClick = () => {
    resetNodesFilter();

    let gridState: State = {
      skip: 0,
      take: 100,
      filter: {
        logic: 'and',
        filters: [],
      },
      group: [{ field: 'LaneNumber' }],
    };
    saveNodesGridState(dispatch, gridState);
  };

  const widgetAttendantStationsCountTileClick = () => {
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.FIELD_NODETYPE_NAME,
          operator: 'eq',
          value: 'Cashier',
        },
      ],
    };
    saveNodesFilters(dispatch, filter);

    resetGridState();
  };

  const widgetMobileAttendantCountTileClick = () => {
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.FIELD_NODETYPE_NAME,
          operator: 'eq',
          value: 'MobileAttendant',
        },
      ],
    };
    saveNodesFilters(dispatch, filter);

    resetGridState();
  };

  return (
    <React.Fragment>
      <Box className={classes.widgetBox}>
        <div className={classes.overViewTitle}>
          <StringResourceManager
            stringKey="ASSETS_SUMMARY"
            style="overViewTitle"
          />
        </div>
        <div className={classes.flexBox}>
          <div className={classes.widgetItem}>
            <div className={classes.totalCountContainer}>
              <WidgetTotalCountTile
                title={GetString('STORES')}
                count={storeRecordsCount}
                bgColor="#5694DD"
                textColor="#fff"
                goToScreen="assets"
                onClick={widgetStoreCountTileClick}
                testId="overview_assets_storesCount"
              />
              <WidgetTotalCountTile
                title={GetString('LANES')}
                count={nodeTypeCounts.Robots + nodeTypeCounts.MasterRobots}
                bgColor="#5694DD"
                textColor="#fff"
                goToScreen="assets"
                onClick={widgetLaneCountTileClick}
                testId="overview_assets_lanesCount"
              />
            </div>
            <div className={classes.totalCountContainer}>
              <WidgetTotalCountTile
                title={GetString('CASHIERS')}
                count={nodeTypeCounts.Cashiers}
                bgColor="#5694DD"
                textColor="#fff"
                goToScreen="assets"
                onClick={widgetAttendantStationsCountTileClick}
                testId="overview_assets_attendantsCount"
              />
              <WidgetTotalCountTile
                title={GetString('MOBIILEATTENDANTS')}
                count={nodeTypeCounts.MobileAttendants}
                bgColor="#5694DD"
                textColor="#fff"
                goToScreen="assets"
                onClick={widgetMobileAttendantCountTileClick}
                testId="overview_assets_mobileAttendantsCount"
              />
            </div>
            <OverviewWidgetPieChart
              chartTitleText={GetString('LANES_BY_SYSTEM_TYPE')}
              seriesData={categoryRecordCounts}
              chartSeriesField={'Total'}
              chartSeriesCategoryField={'Value'}
              chartSeriesClick={onChartSeriesItemClick}
              legendItemClick={onLegendItemClick}
              labelContent={labelContent}
              goToScreen="assets"
              isRefreshing={isFetchingCategoryCountsOverview}
            />
          </div>
        </div>
      </Box>
    </React.Fragment>
  );
}
